/**
 * ambient_critters — the PURE rules of the close-up life (ambient life wave, 2026-09-29): SUNWING butterflies over the
 * meadows by day, GLASSDART dragonflies over still water, TIDECLACK crabs on the sea's beaches. Models
 * `prop:critter_<key>` (Meshy 7 ultra, re-baked; every head faces +Z; the butterfly is modelled upright and laid flat by
 * the client). Every critter lives in a bubble around the viewer — like the pollen and the gnats, the field is
 * everywhere and the bubble moves — sized by the tier's `particleCap`. AtlasCrittersModule owns the world and the draws.
 */
import type { AmbientRng } from './ambient_life.js';

export type CritterKind = 'sunwing' | 'glassdart' | 'tideclack';

export const SUNWING = {
  /** Around the viewer (m) and recycled past 1.4× that. */
  radius: 18,
  height: [0.35, 2.4] as const,
  /** Wingspan (m) — the model is scaled to it. */
  span: [0.07, 0.11] as const,
  speed: [1.1, 2.1] as const,
  /** Wingbeats (Hz) flying and the slow open-close while perched. */
  flapHz: [8, 11] as const,
  restFlapHz: 0.9,
  /** A new heading every this many seconds (the flit). */
  retarget: [0.6, 2.2] as const,
  /** Chance a retarget lands instead, and how long it sits (s). */
  landChance: 0.22,
  land: [2.5, 8] as const,
  /** A person inside this sends a perched one up (m). */
  fleeM: 1.8,
  maxAtCap1: 14,
  /** Wing tints (sRGB): the model's ivory wings are multiplied by one of these. */
  tints: [0xf2a93b, 0x49c2b8, 0xa47cf0, 0xf6f0e2, 0xe68a9a, 0xe8d24a] as const,
} as const;

export const GLASSDART = {
  /** Hunts within this of a water point (m), hovering this high over the surface (m). */
  range: 14,
  height: [0.35, 1.7] as const,
  span: [0.09, 0.13] as const,
  /** Hover for this long (s), then dart this far (m) at this speed (m/s). */
  hover: [0.5, 2.6] as const,
  dart: [1.5, 6] as const,
  dartSpeed: [6, 9.5] as const,
  /** Wing buzz shown (rad/s of the phase) and its amplitude (rad). */
  buzz: 38,
  buzzAmp: 0.3,
  /** Water points are looked for within this of the viewer (m). */
  searchM: 45,
  minTempC: 12,
  maxAtCap1: 8,
} as const;

export const TIDECLACK = {
  /** On the dry beach within this of a sea shore point, inland (m). */
  band: 7,
  /** Width across the splayed legs (m) — the model's normalised width (the shell is about 45 % of it). */
  size: [0.16, 0.26] as const,
  /** Sideways dashes: this far (m) at this speed (m/s), then a pause (s). */
  dash: [0.4, 1.5] as const,
  dashSpeed: [1.2, 2.2] as const,
  pause: [0.6, 3.5] as const,
  /** A person inside this sends it scuttling into the sea (m), at this speed (m/s). */
  fleeM: 6,
  fleeSpeed: 3.2,
  searchM: 60,
  maxAtCap1: 10,
} as const;

// ─── when ────────────────────────────────────────────────────────────────────

export interface CritterEnv {
  enabled: boolean | undefined;
  particleCap: number;
  /** The pollen gate's own conditions (meadow biome, open ground, day, fair, warm): ambient_life's. */
  meadowByDay: boolean;
  night: number;
  precipKind: string;
  precip: number;
  temperatureC: number | null;
  indoors: boolean;
  away: boolean;
  /** Still water points found near the viewer, and sea shore points. */
  waterPoints: number;
  seaShorePoints: number;
}

export interface CritterBudget { sunwing: number; glassdart: number; tideclack: number; why: string }

export function critterBudget(e: CritterEnv): CritterBudget {
  const none = (why: string): CritterBudget => ({ sunwing: 0, glassdart: 0, tideclack: 0, why });
  if (e.enabled === false) return none('ambientLife off');
  const cap = Math.max(0, Math.min(1, e.particleCap || 0));
  if (cap <= 0) return none('particleCap 0');
  if (e.indoors) return none('indoors');
  if (e.away) return none('away from the ground');
  const raining = (e.precipKind === 'rain' || e.precipKind === 'snow') && e.precip > 0.05;
  const temp = e.temperatureC ?? 18;
  const day = e.night <= 0.35;
  const sunwing = e.meadowByDay ? Math.max(3, Math.round(SUNWING.maxAtCap1 * cap)) : 0;
  const glassdart = e.waterPoints > 0 && day && !raining && e.precipKind !== 'dust' && temp >= GLASSDART.minTempC ? Math.max(2, Math.round(GLASSDART.maxAtCap1 * cap)) : 0;
  const tideclack = e.seaShorePoints > 0 && e.precipKind !== 'dust' ? Math.max(3, Math.round(TIDECLACK.maxAtCap1 * cap)) : 0;
  const why = sunwing || glassdart || tideclack ? '' : 'nothing here (no meadow by day, still water or sea beach)';
  return { sunwing, glassdart, tideclack, why };
}

const lerp = (a: readonly [number, number], t: number): number => a[0] + (a[1] - a[0]) * t;

// ─── sunwing ─────────────────────────────────────────────────────────────────

export interface Sunwing {
  x: number; y: number; z: number;
  vx: number; vz: number; vy: number;
  yaw: number;
  span: number; tint: number;
  flap: number; flapPrev: number; flapHz: number;
  timer: number;
  /** The height it cruises at above the ground (m). */
  h: number;
  perched: boolean;
  /** Gliding down to land (it perches when it reaches the grass — never a drop from its cruise height). */
  landing: boolean;
  /** A probe's close-up butterfly: never flushed by a person (the module's lever only). */
  tame?: boolean;
}

export function spawnSunwing(rng: AmbientRng, px: number, pz: number, groundAt: (x: number, z: number) => number): Sunwing {
  const a = rng() * Math.PI * 2, d = Math.sqrt(rng()) * SUNWING.radius;
  const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
  const h = lerp(SUNWING.height, rng());
  const yaw = rng() * Math.PI * 2, sp = lerp(SUNWING.speed, rng());
  return {
    x, y: groundAt(x, z) + h, z, vx: Math.sin(yaw) * sp, vz: Math.cos(yaw) * sp, vy: 0, yaw,
    span: lerp(SUNWING.span, rng()), tint: SUNWING.tints[Math.floor(rng() * SUNWING.tints.length)],
    flap: rng() * Math.PI * 2, flapPrev: 0, flapHz: lerp(SUNWING.flapHz, rng()), timer: lerp(SUNWING.retarget, rng()), h, perched: false, landing: false,
  };
}

/** The wing angle drawn for a flap phase (rad): a butterfly's stroke spends longer with the wings up. */
export function wingAngleAt(perched: boolean, flap: number): number {
  if (perched) return 0.55 + 0.5 * Math.sin(flap); // slow open-close, mostly raised
  return 0.25 + 0.95 * Math.sin(flap);
}
export function sunwingWingAngle(s: Sunwing): number { return wingAngleAt(s.perched, s.flap); }

export function stepSunwing(s: Sunwing, dt: number, rng: AmbientRng, groundY: number, px: number, pz: number, near: { x: number; z: number } | null): void {
  s.flapPrev = s.flap;
  s.timer -= dt;
  const nearD = near ? Math.hypot(near.x - s.x, near.z - s.z) : Infinity;
  if (s.perched) {
    s.flap += SUNWING.restFlapHz * Math.PI * 2 * dt;
    s.y = groundY + 0.04;
    if (s.timer <= 0 || nearD < SUNWING.fleeM) {
      s.perched = false; s.timer = lerp(SUNWING.retarget, rng());
      const away = near && nearD < SUNWING.fleeM ? Math.atan2(s.x - near.x, s.z - near.z) : rng() * Math.PI * 2;
      const sp = lerp(SUNWING.speed, rng()) * 1.3;
      s.vx = Math.sin(away) * sp; s.vz = Math.cos(away) * sp; s.vy = 1.2;
    }
    return;
  }
  s.flap += s.flapHz * Math.PI * 2 * dt;
  if (s.landing && s.timer <= 0) s.landing = false; // a landing that never reached the grass (a slope) gives up
  if (s.timer <= 0 && !s.landing) {
    // WHY the glide (2026-09-29, the logic test): landing used to set `perched` in mid-air and the next step snapped
    // the butterfly from its cruise height (up to 2.4 m) onto the grass — it glides down now, slowing as it comes.
    if (rng() < SUNWING.landChance) { s.landing = true; s.h = 0.04; s.vx *= 0.35; s.vz *= 0.35; s.timer = 5; }
    else {
    // a new flit; drawn back toward the viewer's bubble when it strays past its radius
    let yaw = s.yaw + (rng() - 0.5) * 2.4;
    const dx = px - s.x, dz = pz - s.z;
    if (Math.hypot(dx, dz) > SUNWING.radius) yaw = Math.atan2(dx, dz) + (rng() - 0.5) * 0.8;
    const sp = lerp(SUNWING.speed, rng());
    s.vx = Math.sin(yaw) * sp; s.vz = Math.cos(yaw) * sp;
    s.h = lerp(SUNWING.height, rng());
    s.timer = lerp(SUNWING.retarget, rng());
    }
  }
  // the bob of every wingbeat on top of the cruise height
  const targetY = groundY + s.h + Math.sin(s.flap) * 0.06;
  s.vy += ((targetY - s.y) * 2.2 - s.vy) * Math.min(1, dt * 3);
  s.x += s.vx * dt; s.z += s.vz * dt; s.y += s.vy * dt;
  if (s.landing && s.y - groundY < 0.1) { s.perched = true; s.landing = false; s.y = groundY + 0.04; s.vx = 0; s.vz = 0; s.vy = 0; s.timer = lerp(SUNWING.land, rng()); return; }
  const floor = s.landing ? 0.04 : 0.15;
  if (s.y < groundY + floor) { s.y = groundY + floor; s.vy = Math.abs(s.vy); }
  if (Math.hypot(s.vx, s.vz) > 0.05) s.yaw = Math.atan2(s.vx, s.vz);
}

export function sunwingDone(s: Sunwing, px: number, pz: number): boolean { return Math.hypot(s.x - px, s.z - pz) > SUNWING.radius * 1.4; }

// ─── glassdart ───────────────────────────────────────────────────────────────

export interface WaterPoint { x: number; z: number; y: number }

export interface Glassdart {
  x: number; y: number; z: number;
  yaw: number; span: number;
  home: WaterPoint;
  mode: 'hover' | 'dart';
  timer: number;
  tx: number; ty: number; tz: number; speed: number;
  buzz: number; buzzPrev: number;
  /** A dart that has just started (the module plays the buzz once for a near one). */
  darted: boolean;
  /** A probe's close-up dragonfly: hovers where it was put and never darts (the module's lever only). */
  tame?: boolean;
}

function glassdartTarget(g: Glassdart, rng: AmbientRng): void {
  const a = rng() * Math.PI * 2, d = lerp(GLASSDART.dart, rng());
  let tx = g.x + Math.cos(a) * d, tz = g.z + Math.sin(a) * d;
  // keep to the water point's range
  const hx = tx - g.home.x, hz = tz - g.home.z, hd = Math.hypot(hx, hz);
  if (hd > GLASSDART.range) { tx = g.home.x + (hx / hd) * GLASSDART.range * 0.7; tz = g.home.z + (hz / hd) * GLASSDART.range * 0.7; }
  g.tx = tx; g.tz = tz; g.ty = g.home.y + lerp(GLASSDART.height, rng());
  g.speed = lerp(GLASSDART.dartSpeed, rng());
}

export function spawnGlassdart(rng: AmbientRng, home: WaterPoint): Glassdart {
  const a = rng() * Math.PI * 2, d = rng() * GLASSDART.range * 0.6;
  const g: Glassdart = {
    x: home.x + Math.cos(a) * d, y: home.y + lerp(GLASSDART.height, rng()), z: home.z + Math.sin(a) * d,
    yaw: rng() * Math.PI * 2, span: lerp(GLASSDART.span, rng()), home, mode: 'hover', timer: lerp(GLASSDART.hover, rng()),
    tx: 0, ty: 0, tz: 0, speed: 0, buzz: rng() * 10, buzzPrev: 0, darted: false,
  };
  return g;
}

export function stepGlassdart(g: Glassdart, dt: number, rng: AmbientRng): void {
  g.buzzPrev = g.buzz;
  g.buzz += GLASSDART.buzz * dt;
  g.darted = false;
  if (g.mode === 'hover') {
    g.timer -= dt;
    g.y += Math.sin(g.buzz * 0.05) * 0.03 * dt;
    if (g.timer <= 0) { glassdartTarget(g, rng); g.mode = 'dart'; g.darted = true; g.yaw = Math.atan2(g.tx - g.x, g.tz - g.z); }
    return;
  }
  const dx = g.tx - g.x, dy = g.ty - g.y, dz = g.tz - g.z, dl = Math.hypot(dx, dy, dz);
  const step = g.speed * dt;
  if (dl <= step) { g.x = g.tx; g.y = g.ty; g.z = g.tz; g.mode = 'hover'; g.timer = lerp(GLASSDART.hover, rng()); return; }
  g.x += (dx / dl) * step; g.y += (dy / dl) * step; g.z += (dz / dl) * step;
}

// ─── tideclack ───────────────────────────────────────────────────────────────

export interface ShorePoint { x: number; z: number; y: number; /** Unit direction from the beach toward the sea. */ sx: number; sz: number }

export interface Tideclack {
  x: number; z: number;
  /** The shell faces `yaw` (+Z forward); it walks SIDEWAYS, along ±(cos yaw, −sin yaw). */
  yaw: number;
  size: number;
  shore: ShorePoint;
  mode: 'pause' | 'dash' | 'flee';
  timer: number;
  dir: number;
  speed: number;
  gait: number; gaitPrev: number;
  /** Reached the water while fleeing: the module drops it. */
  gone: boolean;
  /** A dash that has just started (the module clicks for a near one). */
  started: boolean;
  /** A probe's close-up crab: ignores people and holds its pause (the module's lever only). */
  tame?: boolean;
}

export function spawnTideclack(rng: AmbientRng, shore: ShorePoint): Tideclack {
  const inland = rng() * TIDECLACK.band, along = (rng() - 0.5) * 14;
  const x = shore.x - shore.sx * inland + -shore.sz * along, z = shore.z - shore.sz * inland + shore.sx * along;
  // it faces the sea or away, and so walks along the waterline
  const yaw = Math.atan2(shore.sx, shore.sz) + (rng() < 0.5 ? 0 : Math.PI) + (rng() - 0.5) * 0.6;
  return { x, z, yaw, size: lerp(TIDECLACK.size, rng()), shore, mode: 'pause', timer: lerp(TIDECLACK.pause, rng()), dir: rng() < 0.5 ? -1 : 1, speed: 0, gait: rng() * 6, gaitPrev: 0, gone: false, started: false };
}

export function stepTideclack(c: Tideclack, dt: number, rng: AmbientRng, near: { x: number; z: number } | null, groundBelowSea: (x: number, z: number) => boolean): void {
  c.gaitPrev = c.gait;
  c.started = false;
  const nearD = near ? Math.hypot(near.x - c.x, near.z - c.z) : Infinity;
  if (c.mode !== 'flee' && nearD < TIDECLACK.fleeM) {
    // turn so the sea is to one side, and run for it
    c.mode = 'flee'; c.speed = TIDECLACK.fleeSpeed; c.started = true;
    const sideX = Math.cos(c.yaw), sideZ = -Math.sin(c.yaw);
    c.dir = sideX * c.shore.sx + sideZ * c.shore.sz >= 0 ? 1 : -1;
  }
  if (c.mode === 'pause') {
    c.timer -= dt;
    if (c.timer <= 0) { c.mode = 'dash'; c.timer = lerp(TIDECLACK.dash, rng()) / lerp(TIDECLACK.dashSpeed, rng()); c.speed = lerp(TIDECLACK.dashSpeed, rng()); c.dir = rng() < 0.5 ? -1 : 1; c.started = true; }
    return;
  }
  const sideX = Math.cos(c.yaw), sideZ = -Math.sin(c.yaw);
  c.x += sideX * c.dir * c.speed * dt;
  c.z += sideZ * c.dir * c.speed * dt;
  c.gait += c.speed * 14 * dt;
  if (c.mode === 'flee') {
    if (groundBelowSea(c.x, c.z)) c.gone = true;
    return;
  }
  // a dash that strays up the beach turns back toward the waterline
  const inland = -((c.x - c.shore.x) * c.shore.sx + (c.z - c.shore.z) * c.shore.sz);
  if (inland > TIDECLACK.band * 1.6) c.dir = (sideX * c.shore.sx + sideZ * c.shore.sz >= 0) ? 1 : -1;
  c.timer -= dt;
  if (c.timer <= 0) { c.mode = 'pause'; c.timer = lerp(TIDECLACK.pause, rng()); c.speed = 0; }
}
